<script setup lang="ts">
  import { computed } from 'vue';
  import BriefcaseIcon from '@/shared/assets/icons/briefcase.svg';
  import CheckMarkCircleIcon from '@/shared/assets/icons/CheckMarkCircleIcon.svg';
  import EraserIcon from '@/shared/assets/icons/EraserIcon.svg';
  import UserAdminIcon from '@/shared/assets/icons/UserAdminIcon.svg';
  import { AccordionContent, AccordionItem, ColoredButton } from '@/shared/ui';
  import PermissionsAccordionTrigger from './PermissionsAccordionTrigger.vue';
  import PermissionSubjectList from './PermissionSubjectList.vue';
  import {
    permissionSubjectSelectAllLabel,
    permissionSubjectSelectLabel,
  } from '../../lib/permission-subject-filters';
  import type {
    PermissionSubject,
    PermissionSubjectMode,
  } from '../../composables/usePermissionSubjects';

  const props = withDefaults(
    defineProps<{
      mode: PermissionSubjectMode;
      items: PermissionSubject[];
      selectedIds?: string[];
      loading?: boolean;
      hasError?: boolean;
      disabled?: boolean;
      applyDisabled?: boolean;
      clearDisabled?: boolean;
    }>(),
    {
      selectedIds: () => [],
      loading: false,
      hasError: false,
      disabled: false,
      applyDisabled: true,
      clearDisabled: true,
    }
  );

  const emit = defineEmits<{
    (e: 'update:selectedIds', value: string[]): void;
    (e: 'apply'): void;
    (e: 'clear'): void;
  }>();

  const isPositions = computed(() => props.mode === 'positions');

  const accordionValue = computed(() =>
    isPositions.value ? 'positions' : 'functional-activities'
  );
  const title = computed(() => permissionSubjectSelectAllLabel(props.mode));
  const headerIcon = computed(() => (isPositions.value ? BriefcaseIcon : UserAdminIcon));
  const selectLabel = computed(() => permissionSubjectSelectLabel(props.mode));
</script>

<template>
  <AccordionItem
    :value="accordionValue"
    class="rounded-lg border border-gray-02 p-4 last:border-b"
    :data-testid="`permission-subject-accordion-${mode}`"
  >
    <PermissionsAccordionTrigger :icon="headerIcon">
      {{ title }}
    </PermissionsAccordionTrigger>

    <AccordionContent class="pt-4 pb-0">
      <div
        class="flex flex-col gap-2"
        :class="{ 'opacity-60': disabled }"
        :inert="disabled"
        :data-disabled="disabled"
        :data-testid="`permission-subject-content-${mode}`"
      >
        <p class="text-sm text-gray-11">{{ selectLabel }}</p>

        <PermissionSubjectList
          :mode="mode"
          :items="items"
          :selected-ids="selectedIds"
          :loading="loading"
          :has-error="hasError"
          :disabled="disabled"
          @update:selected-ids="emit('update:selectedIds', $event)"
        />

        <div class="mt-1 flex gap-4">
          <ColoredButton
            class="!h-[42px] flex-1 justify-center !px-4"
            text="Aplicar"
            :icon="CheckMarkCircleIcon"
            icon-alt="Aplicar"
            :disabled="disabled || applyDisabled"
            :data-testid="`permission-subject-apply-${mode}`"
            @click="emit('apply')"
          />
          <ColoredButton
            class="!h-[42px] flex-1 justify-center !px-4"
            text="Limpar"
            :icon="EraserIcon"
            icon-alt="Limpar"
            outlined
            :disabled="disabled || clearDisabled"
            :data-testid="`permission-subject-clear-${mode}`"
            @click="emit('clear')"
          />
        </div>
      </div>
    </AccordionContent>
  </AccordionItem>
</template>
